import React, { useState, useEffect, useRef } from 'react';
import { API, COLORS } from '../utils/constants';

// ── Markdown renderer ─────────────────────────────────────────────────────────

function renderInline(text) {
  const parts = [];
  const regex = /(\*\*[^*]+\*\*|`[^`]+`)/g;
  let last = 0, match;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > last) parts.push(text.slice(last, match.index));
    const m = match[0];
    if (m.startsWith('**')) {
      parts.push(<strong key={match.index}>{m.slice(2, -2)}</strong>);
    } else {
      parts.push(<code key={match.index} style={{ background: 'rgba(255,107,53,0.18)', color: COLORS.accent, padding: '1px 5px', borderRadius: 4, fontSize: '0.88em' }}>{m.slice(1, -1)}</code>);
    }
    last = match.index + m.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts.length === 0 ? text : parts.length === 1 && typeof parts[0] === 'string' ? parts[0] : parts;
}

function renderMarkdown(text) {
  return text.split('\n').map((line, i) => {
    if (line.startsWith('### ')) return <div key={i} style={{ fontWeight: 800, fontSize: 13, color: COLORS.accent, marginTop: 10, marginBottom: 3 }}>{line.slice(4)}</div>;
    if (line.startsWith('## '))  return <div key={i} style={{ fontWeight: 900, fontSize: 14, color: COLORS.text,   marginTop: 12, marginBottom: 4 }}>{line.slice(3)}</div>;
    if (line.startsWith('# '))   return <div key={i} style={{ fontWeight: 900, fontSize: 16, color: '#fff',        marginTop: 14, marginBottom: 6 }}>{line.slice(2)}</div>;
    if (line.startsWith('- ') || line.startsWith('* ')) {
      return <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 3 }}><span style={{ color: COLORS.accent, flexShrink: 0, lineHeight: 1.5 }}>·</span><span style={{ lineHeight: 1.5 }}>{renderInline(line.slice(2))}</span></div>;
    }
    const numbered = line.match(/^(\d+)\. (.+)/);
    if (numbered) {
      return <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 3 }}><span style={{ color: COLORS.accent, flexShrink: 0, minWidth: 18, lineHeight: 1.5 }}>{numbered[1]}.</span><span style={{ lineHeight: 1.5 }}>{renderInline(numbered[2])}</span></div>;
    }
    if (line === '') return <div key={i} style={{ height: 6 }} />;
    return <div key={i} style={{ lineHeight: 1.55, marginBottom: 1 }}>{renderInline(line)}</div>;
  });
}

// ── Redimensionar imagen en canvas (max 1024px) ───────────────────────────────

function resizeImage(file) {
  return new Promise((resolve) => {
    const MAX = 1024;
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let w = img.width, h = img.height;
        if (w > MAX || h > MAX) {
          if (w > h) { h = Math.round(h * MAX / w); w = MAX; }
          else        { w = Math.round(w * MAX / h); h = MAX; }
        }
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
        const b64 = dataUrl.split(',')[1];
        resolve({ b64, mediaType: 'image/jpeg', preview: dataUrl });
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
}

// ── Indicador de "pensando" ───────────────────────────────────────────────────

function ThinkingDots() {
  const [step, setStep] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setStep(s => (s + 1) % 4), 400);
    return () => clearInterval(t);
  }, []);
  return <span style={{ color: COLORS.textMuted, fontSize: 15, letterSpacing: 3 }}>{['·', '· ·', '· · ·', '· ·'][step]}</span>;
}

// ── Formatear fecha relativa ──────────────────────────────────────────────────

function fmtDate(dt) {
  if (!dt) return '—';
  const d = new Date(dt.includes('T') ? dt : dt.replace(' ', 'T'));
  const now = new Date();
  const diff = Math.floor((now - d) / 86400000);
  const time = d.toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' });
  if (diff === 0) return 'Hoy ' + time;
  if (diff === 1) return 'Ayer ' + time;
  if (diff < 7)  return d.toLocaleDateString('es', { weekday: 'long' }) + ' ' + time;
  return d.toLocaleDateString('es', { day: 'numeric', month: 'short', year: diff > 365 ? 'numeric' : undefined }) + ' · ' + time;
}

// ── ChatScreen ────────────────────────────────────────────────────────────────

export default function ChatScreen({ currentUser, onBack, initialPrompt, onPromptConsumed }) {
  const [messages, setMessages]     = useState([]);
  const [input, setInput]           = useState('');
  const [thinking, setThinking]     = useState(false);
  const [image, setImage]           = useState(null);

  // History panel states
  const [showHistory, setShowHistory]   = useState(false);
  const [histSessions, setHistSessions] = useState([]);
  const [histLoading, setHistLoading]   = useState(false);
  const [viewSession, setViewSession]   = useState(null); // { id, messages[] }
  const [viewLoading, setViewLoading]   = useState(false);

  const bottomRef      = useRef(null);
  const textareaRef    = useRef(null);
  const fileInputRef   = useRef(null);
  const autoPromptSent = useRef(false);
  const sessionId      = useRef(String(Date.now())); // Fresh session per mount

  // Scroll al fondo
  useEffect(() => {
    if (!showHistory) bottomRef.current?.scrollIntoView({ behavior: messages.length <= 2 ? 'instant' : 'smooth' });
  }, [messages, thinking, showHistory]);

  // Auto-resize textarea
  const handleInputChange = (e) => {
    setInput(e.target.value);
    const el = textareaRef.current;
    if (el) { el.style.height = 'auto'; el.style.height = Math.min(el.scrollHeight, 120) + 'px'; }
  };

  // Selección de imagen
  const handleImageSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const resized = await resizeImage(file);
    setImage(resized);
    e.target.value = '';
  };

  const removeImage = () => setImage(null);
  const canSend = (input.trim() || image) && !thinking;

  // Auto-send initialPrompt al montar
  useEffect(() => {
    if (!initialPrompt || autoPromptSent.current) return;
    autoPromptSent.current = true;
    sendMessageWith(initialPrompt);
    if (onPromptConsumed) onPromptConsumed();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sendMessageWith = async (text) => {
    if (!text || thinking) return;
    setMessages(prev => [...prev, { id: Date.now(), role: 'user', content: text }]);
    setThinking(true);
    try {
      const res = await fetch(API + '/api/chat', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ message: text, session_id: sessionId.current }),
      });
      const data = await res.json();
      if (data.session_id) sessionId.current = data.session_id;
      setMessages(prev => [...prev, { id: Date.now() + 1, role: 'assistant', content: data.response || 'Sin respuesta.', tools_used: data.tools_used || [] }]);
    } catch {
      setMessages(prev => [...prev, { id: Date.now() + 1, role: 'assistant', content: 'Error de conexión. Inténtalo de nuevo.' }]);
    }
    setThinking(false);
  };

  const sendMessage = async () => {
    if (!canSend) return;
    const text    = input.trim();
    const imgSnap = image;
    setMessages(prev => [...prev, { id: Date.now(), role: 'user', content: text, imagePreview: imgSnap?.preview || null }]);
    setInput('');
    setImage(null);
    if (textareaRef.current) textareaRef.current.style.height = '44px';
    setThinking(true);
    try {
      const body = { message: text, session_id: sessionId.current };
      if (imgSnap) { body.image_b64 = imgSnap.b64; body.image_media_type = imgSnap.mediaType; }
      const res = await fetch(API + '/api/chat', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.session_id) sessionId.current = data.session_id;
      setMessages(prev => [...prev, { id: Date.now() + 1, role: 'assistant', content: data.response || 'Sin respuesta.', tools_used: data.tools_used || [] }]);
    } catch {
      setMessages(prev => [...prev, { id: Date.now() + 1, role: 'assistant', content: 'Error de conexión. Inténtalo de nuevo.' }]);
    }
    setThinking(false);
  };

  // ── History panel ───────────────────────────────────────────────

  const openHistory = async () => {
    setShowHistory(true);
    setViewSession(null);
    setHistLoading(true);
    setHistSessions([]);
    try {
      const r = await fetch(API + '/api/chat/sessions', { credentials: 'include' });
      if (r.ok) setHistSessions(await r.json());
    } catch {}
    setHistLoading(false);
  };

  const openSession = async (sid) => {
    setViewLoading(true);
    setViewSession({ id: sid, messages: [] });
    try {
      const r = await fetch(API + '/api/chat/sessions/' + sid, { credentials: 'include' });
      if (r.ok) setViewSession({ id: sid, messages: await r.json() });
    } catch {}
    setViewLoading(false);
  };

  const closeHistory = () => { setShowHistory(false); setViewSession(null); };

  const continueSession = (sid, msgs) => {
    setMessages([]);  // Limpiar mensajes primero
    sessionId.current = sid;
    setMessages(msgs.map((m, i) => ({ id: i, role: m.role, content: m.content })));
    closeHistory();
  };

  // ── Bubble component ───────────────────────────────────────────

  const Bubble = ({ msg }) => (
    <div style={{ display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start', marginBottom: 14, alignItems: 'flex-end', gap: 8 }}>
      {msg.role === 'assistant' && (
        <div style={{ width: 26, height: 26, borderRadius: 8, background: 'linear-gradient(135deg, #ff6b35, #ff3366)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 11 }}>⚡</div>
      )}
      <div style={{ maxWidth: '80%', background: msg.role === 'user' ? 'rgba(255,107,53,0.12)' : 'rgba(255,255,255,0.04)', border: `1px solid ${msg.role === 'user' ? 'rgba(255,107,53,0.3)' : 'rgba(255,255,255,0.07)'}`, borderRadius: msg.role === 'user' ? '18px 18px 4px 18px' : '18px 18px 18px 4px', padding: '10px 14px', fontSize: 14, color: COLORS.text }}>
        {msg.imagePreview && (
          <img src={msg.imagePreview} alt="" style={{ width: '100%', maxWidth: 220, borderRadius: 10, marginBottom: msg.content ? 8 : 0, display: 'block' }} />
        )}
        {msg.role === 'assistant' ? renderMarkdown(msg.content) : msg.content}
        {msg.tools_used?.length > 0 && (
          <div style={{ marginTop: 8, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {msg.tools_used.map(t => (
              <span key={t} style={{ fontSize: 9, color: 'rgba(255,107,53,0.6)', background: 'rgba(255,107,53,0.08)', border: '1px solid rgba(255,107,53,0.15)', borderRadius: 6, padding: '2px 6px', letterSpacing: 0.3 }}>⚡ {t.replace(/_/g, ' ')}</span>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  // ── Render ──────────────────────────────────────────────────────

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: '#0a0a0f', position: 'relative', overflow: 'hidden' }}>

      {/* ── History panel overlay ─────────────────────────────── */}
      {showHistory && (
        <div style={{ position: 'absolute', inset: 0, background: '#0a0a0f', zIndex: 50, display: 'flex', flexDirection: 'column' }}>

          {/* History header */}
          <div style={{ padding: '52px 20px 14px', background: 'linear-gradient(180deg, #0a0a0f 80%, transparent 100%)', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
            <button
              onClick={() => viewSession ? setViewSession(null) : closeHistory()}
              style={{ background: 'none', border: 'none', color: COLORS.textMuted, fontSize: 24, cursor: 'pointer', padding: '4px 8px', lineHeight: 1, flexShrink: 0 }}
            >←</button>
            <div style={{ flex: 1 }}>
              <div style={{ color: COLORS.accent, fontSize: 9, letterSpacing: 2.5, fontWeight: 800, textTransform: 'uppercase' }}>FitCoach</div>
              <div style={{ color: '#fff', fontSize: 16, fontWeight: 900, letterSpacing: -0.3 }}>
                {viewSession
                  ? (viewSession.messages[0]?.creado ? fmtDate(viewSession.messages[0].creado) : 'Conversación')
                  : 'Conversaciones'}
              </div>
            </div>
          </div>

          {/* History content */}
          <div style={{ flex: 1, overflowY: 'auto', padding: viewSession && !viewLoading && viewSession.messages.length > 0 ? '14px 16px 80px' : '14px 16px' }}>

            {/* Sessions list */}
            {!viewSession && (
              histLoading
                ? <div style={{ textAlign: 'center', paddingTop: 60, color: COLORS.textMuted, fontSize: 13 }}>Cargando...</div>
                : histSessions.length === 0
                ? (
                  <div style={{ textAlign: 'center', paddingTop: 60 }}>
                    <div style={{ fontSize: 32, marginBottom: 14 }}>💬</div>
                    <div style={{ color: COLORS.textMuted, fontSize: 13 }}>Sin conversaciones anteriores</div>
                  </div>
                )
                : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {histSessions.map(s => (
                      <button
                        key={s.session_id}
                        onClick={() => openSession(s.session_id)}
                        style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 14, padding: '13px 15px', textAlign: 'left', cursor: 'pointer', width: '100%' }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                          <div style={{ fontSize: 11, color: COLORS.accent, fontWeight: 700, letterSpacing: 0.3 }}>{fmtDate(s.inicio)}</div>
                          <div style={{ fontSize: 10, color: COLORS.textDim, background: 'rgba(255,255,255,0.06)', borderRadius: 8, padding: '2px 7px' }}>{Math.floor(s.mensajes / 2)} msgs</div>
                        </div>
                        <div style={{ fontSize: 13, color: COLORS.textMuted, lineHeight: 1.45, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                          {s.primer_mensaje || '(sin texto)'}
                        </div>
                      </button>
                    ))}
                  </div>
                )
            )}

            {/* Session messages (read-only) */}
            {viewSession && (
              viewLoading
                ? <div style={{ textAlign: 'center', paddingTop: 60, color: COLORS.textMuted, fontSize: 13 }}>Cargando...</div>
                : viewSession.messages.length === 0
                ? <div style={{ textAlign: 'center', paddingTop: 60, color: COLORS.textMuted, fontSize: 13 }}>Conversación vacía</div>
                : <div style={{ paddingBottom: 24 }}>
                    {viewSession.messages.map((msg, i) => (
                      <Bubble key={i} msg={msg} />
                    ))}
                  </div>
            )}
          </div>

          {/* Continuar conversación */}
          {viewSession && !viewLoading && viewSession.messages.length > 0 && (
            <div style={{ padding: '10px 16px calc(16px + env(safe-area-inset-bottom))', borderTop: '1px solid rgba(255,255,255,0.06)', background: 'rgba(10,10,15,0.97)', flexShrink: 0 }}>
              <button
                onClick={() => continueSession(viewSession.id, viewSession.messages)}
                style={{ width: '100%', padding: '13px 0', background: 'linear-gradient(135deg, #ff6b35, #ff3366)', border: 'none', borderRadius: 14, color: '#fff', fontSize: 15, fontWeight: 800, cursor: 'pointer', letterSpacing: 0.2 }}
              >
                Continuar esta conversación →
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── Main chat ─────────────────────────────────────────── */}

      {/* Header */}
      <div style={{ padding: '52px 20px 14px', background: 'linear-gradient(180deg, #0a0a0f 80%, transparent 100%)', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: COLORS.textMuted, fontSize: 24, cursor: 'pointer', padding: '4px 8px', lineHeight: 1 }}>←</button>
        <div style={{ textAlign: 'center' }}>
          <div style={{ color: COLORS.accent, fontSize: 9, letterSpacing: 2.5, fontWeight: 800, textTransform: 'uppercase' }}>AI Agent</div>
          <div style={{ color: '#fff', fontSize: 17, fontWeight: 900, letterSpacing: -0.3, marginTop: 1 }}>FitCoach</div>
        </div>
        <button
          onClick={openHistory}
          style={{ background: 'none', border: 'none', color: COLORS.textMuted, fontSize: 20, cursor: 'pointer', padding: '4px 8px', lineHeight: 1 }}
        >📋</button>
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 16px 8px' }}>

        {messages.length === 0 && (
          <div style={{ textAlign: 'center', paddingTop: 56 }}>
            <div style={{ width: 64, height: 64, borderRadius: 20, background: 'linear-gradient(135deg, #ff6b35 0%, #ff3366 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', fontSize: 28 }}>⚡</div>
            <div style={{ color: '#fff', fontSize: 18, fontWeight: 800, marginBottom: 10 }}>Hola, {currentUser?.nombre || 'atleta'}</div>
            <div style={{ color: COLORS.textMuted, fontSize: 13, lineHeight: 1.7, maxWidth: 260, margin: '0 auto' }}>
              Soy tu asistente personal de fitness y nutrición. Pregúntame lo que necesites o envíame una foto de una máquina o de tu postura.
            </div>
            <div style={{ marginTop: 28, display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 280, margin: '28px auto 0' }}>
              {[
                '¿Qué como hoy para cumplir mis macros?',
                '¿Cuánto peso hice en press inclinado la última vez?',
                '📷 Foto de máquina → identificar ejercicio',
              ].map(s => (
                <button key={s} onClick={() => { if (!s.startsWith('📷')) { setInput(s); textareaRef.current?.focus(); } else { fileInputRef.current?.click(); } }}
                  style={{ background: 'rgba(255,107,53,0.08)', border: '1px solid rgba(255,107,53,0.2)', borderRadius: 12, padding: '10px 14px', color: COLORS.textMuted, fontSize: 12, cursor: 'pointer', textAlign: 'left', lineHeight: 1.4 }}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map(msg => <Bubble key={msg.id} msg={msg} />)}

        {thinking && (
          <div style={{ display: 'flex', marginBottom: 14, alignItems: 'flex-end', gap: 8 }}>
            <div style={{ width: 26, height: 26, borderRadius: 8, background: 'linear-gradient(135deg, #ff6b35, #ff3366)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 11 }}>⚡</div>
            <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '18px 18px 18px 4px', padding: '12px 16px' }}>
              <ThinkingDots />
            </div>
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {/* Preview imagen */}
      {image && (
        <div style={{ padding: '8px 14px 0', flexShrink: 0 }}>
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <img src={image.preview} alt="" style={{ height: 72, borderRadius: 10, border: '1px solid rgba(255,107,53,0.4)', display: 'block' }} />
            <button onClick={removeImage} style={{ position: 'absolute', top: -6, right: -6, width: 20, height: 20, borderRadius: '50%', background: '#ff3366', border: 'none', color: '#fff', fontSize: 11, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>✕</button>
          </div>
        </div>
      )}

      {/* Input bar */}
      <div style={{ padding: '10px 14px calc(14px + env(safe-area-inset-bottom))', borderTop: '1px solid rgba(255,255,255,0.06)', background: 'rgba(10,10,15,0.97)', display: 'flex', gap: 8, alignItems: 'flex-end', flexShrink: 0 }}>
        <input ref={fileInputRef} type="file" accept="image/*" capture="environment" onChange={handleImageSelect} style={{ display: 'none' }} />
        <button onClick={() => fileInputRef.current?.click()} style={{ width: 44, height: 44, background: image ? 'rgba(255,107,53,0.15)' : 'rgba(255,255,255,0.06)', border: `1px solid ${image ? 'rgba(255,107,53,0.4)' : 'rgba(255,255,255,0.1)'}`, borderRadius: 14, color: image ? COLORS.accent : COLORS.textMuted, fontSize: 18, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'all 0.2s' }}>
          📷
        </button>
        <textarea
          ref={textareaRef}
          value={input}
          onChange={handleInputChange}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
          placeholder={image ? 'Pregunta sobre la imagen...' : 'Pregunta a FitCoach...'}
          rows={1}
          style={{ flex: 1, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 16, padding: '11px 14px', color: COLORS.text, fontSize: 15, resize: 'none', outline: 'none', fontFamily: 'inherit', height: 44, maxHeight: 120, lineHeight: 1.4, overflowY: 'auto' }}
        />
        <button onClick={sendMessage} disabled={!canSend}
          style={{ width: 44, height: 44, background: canSend ? COLORS.accent : 'rgba(255,255,255,0.07)', border: 'none', borderRadius: 14, color: '#fff', fontSize: 20, cursor: canSend ? 'pointer' : 'default', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'background 0.2s' }}>↑</button>
      </div>
    </div>
  );
}
